/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2025 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { consume } from '@lit/context';
import { t } from 'i18next';
import { css, html, LitElement, nothing, PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';

import {
  getShowPrintExampleForConfiguration,
  PrintExampleDto2,
  PrintOptionPerPositionDto,
  SelectedPrintOptionDto,
  StepEnum,
} from '@/api-sdk';
import { FgFullscreenImage, FgPersonalisation } from '@/components';
import { iconChevron, iconStopwatch } from '@/components/icons';
import {
  ConfiguratorContextType,
  configuratorGlobal,
  global,
  GlobalComponentType,
  MobileContext,
  personalisation,
  PersonalisationType,
  printExample,
  visualDesigner,
  VisualDesignerContextType,
} from '@/context';
import {
  ANIMATION_SPEED,
  BREAKPOINT_LARGE_PX,
  BREAKPOINT_MEDIUM_PX,
} from '@/settings';
import {
  baseStyles,
  buttonStyles,
  formStyles,
  typographyStyles,
} from '@/styles';
import { UploadStatus } from '@/types';
import { getPersonalisationData } from '@/utils';

import '@/components/configurator/fg-configurator-step';
import '@/components/configurator/step/wholesale/print-option/fg-print-option-color-count';
import '@/components/configurator/step/wholesale/print-option/fg-print-option-select';
import '@/components/fullscreen-image/fg-fullscreen-image';
import '@/components/image/fg-image';
import '@/components/personalisation/fg-personalisation-status-bar';
import '@/components/print-example/fg-print-example';

@customElement('fg-configurator-print-option-per-position')
export class FgConfiguratorPrintOptionPerPosition extends LitElement {
  private fgPersonalisationDialogRef: Ref<FgPersonalisation> = createRef();

  @property({ type: Array })
  printPositions?: PrintOptionPerPositionDto[] | undefined;

  /**
   * printExampleData {@link PrintExampleDto2} contains the print example data like uuid, filename, url
   * @property {PrintExampleDto} printExampleData
   */
  @property({ type: Object })
  printExampleData?: {
    configurationId: string;
    data: PrintExampleDto2;
  };

  @property({ type: Array })
  showPrintOptions: { [key: number]: boolean | undefined } = {};

  @state()
  selectedPrintPosition: { [key: number]: boolean } = {};

  @state()
  selectedDescription: { [key: number]: string | undefined } = {};

  @state()
  currentSelectedPrintOption?: SelectedPrintOptionDto;

  @state()
  printExampleStatus!: UploadStatus;

  @state()
  firstRender = true;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  @consume({ context: configuratorGlobal.resetContext, subscribe: true })
  configuratorReset = false;

  @consume({ context: MobileContext, subscribe: true })
  isMobile: boolean = false;

  @consume({ context: personalisation.context, subscribe: true })
  personalisationContext: PersonalisationType[] = [];

  @consume({ context: visualDesigner.context, subscribe: true })
  visualDesignerContext!: VisualDesignerContextType;

  fullscreenImageDialogRefs: Ref<FgFullscreenImage>[] = [];

  /**
   * This handleSelectedPrintPosition is only for toggleing the green selected state of the selected print position
   */
  handleSelectedPrintPosition(
    index: number,
    selected: boolean,
    description?: string,
    code?: string,
  ) {
    this.selectedPrintPosition = {
      ...this.selectedPrintPosition,
      [index]: selected,
    };

    this.selectedDescription = {
      ...this.selectedDescription,
      [index]: description,
    };

    /**
     * If print option is changed, remove the personalisation data for the print option
     */
    if (code) {
      personalisation.providerRemoveEvent(this, code);
    }

    /**
     * If all selectedPrintPositions are false of there aren't any reset print example state.
     */
    if (
      Object.values(this.selectedPrintPosition).every(value => value === false)
    ) {
      printExample.updateProviderEvent(this, undefined);
      this.printExampleStatus = UploadStatus.INITIAL;
    }
  }

  openPersonalisationDialog(
    selectedPrintOption: SelectedPrintOptionDto | undefined | null,
  ) {
    if (!selectedPrintOption) {
      return;
    }
    this.currentSelectedPrintOption = selectedPrintOption;
    this.fgPersonalisationDialogRef.value?.openDialog();
  }

  togglePrintOptions(index: number) {
    this.showPrintOptions = {
      ...this.showPrintOptions,
      [index]: !this.showPrintOptions[index],
    };
  }

  protected willUpdate(
    _changedProperties: PropertyValueMap<FgConfiguratorPrintOptionPerPosition>,
  ) {
    if (_changedProperties.has('printPositions') && this.configuratorReset) {
      this.selectedDescription = {};
      this.selectedPrintPosition = {};
      this.showPrintOptions = {};
      configuratorGlobal.updateResetProviderUpdate(this, false);
    }
  }

  async firstUpdated() {
    const { configurationId } = this.configuratorContext;

    if (this.printPositions && configurationId) {
      await getPersonalisationData(this, configurationId);

      this.preFillPrintPosition();

      await this.preFillPrintExample();

      configuratorGlobal.updateGlobalProviderEvent(this, {
        ...this.configuratorContext,
        configurationId: '',
      });
    }
  }

  preFillPrintPosition() {
    if (this.configuratorContext.configurationId && this.printPositions) {
      this.printPositions.find((printPosition, index) =>
        printPosition.methods?.find(printPositionMethod => {
          if (printPositionMethod?.selected) {
            this.handleSelectedPrintPosition(
              index,
              true,
              printPositionMethod?.description,
            );
          }
        }),
      );
    }
  }

  async preFillPrintExample() {
    const { configurationId } = this.configuratorContext;

    if (!configurationId) {
      return;
    }

    const printExampleData = await getShowPrintExampleForConfiguration({
      configurationId: configurationId,
    });

    this.printExampleData = {
      configurationId: configurationId,
      data: printExampleData,
    };
  }

  isColorInputDisabled(selectedPrintOption?: SelectedPrintOptionDto | null) {
    if (!selectedPrintOption) {
      return false;
    }

    return Boolean(
      this.visualDesignerContext?.selectedPrintOptions?.find(
        printOption => printOption.code === selectedPrintOption?.code,
      ) &&
      personalisation.getDataWithCode(
        this.personalisationContext,
        selectedPrintOption?.code,
      )?.visualDesignId,
    );
  }

  handleVisualDesignerColorCountUpdate(
    updatedColors: Array<{ code: string; colorCount: number }>,
  ) {
    if (!this.printPositions) {
      return;
    }

    for (const printPosition of this.printPositions) {
      const code = printPosition.selectedPrintOption?.code;
      if (!code) continue;

      const matchingColor = updatedColors.find(color => color.code === code);
      if (!matchingColor) continue;

      const unselectedOption = printPosition.colorCountOptions?.find(
        option =>
          option?.count === matchingColor.colorCount && !option.selected,
      );

      if (unselectedOption) {
        this.dispatchEvent(
          new CustomEvent('option-selected', {
            composed: true,
            detail: { option: { queryString: unselectedOption.queryString } },
          }),
        );
      }
    }
  }

  resetColorCount(code: string) {
    if (!this.printPositions || !code) {
      return;
    }

    const resetedPrintPosition = this.printPositions.find(
      printPosition => printPosition.selectedPrintOption?.code === code,
    );

    if (!resetedPrintPosition) {
      return;
    }

    const resetedColorCount = resetedPrintPosition.colorCountOptions?.[0];

    if (!resetedColorCount) {
      return;
    }

    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: {
          option: {
            queryString: resetedColorCount?.queryString,
          },
        },
      }),
    );
  }

  render() {
    const { layout } = this.globalContext;
    const { salable, configurationId, productName } = this.configuratorContext;

    return html`
      <fg-configurator-step
        part=${layout}
        .stepType=${StepEnum.print_option_per_position}
      >
        <fg-step-header
          part=${layout}
          slot="step-header"
          .stepType=${StepEnum.print_option_per_position}
        ></fg-step-header>
        <div class="wrapper">
          ${this.printPositions?.map((printPosition, index) => {
            const {
              thumbnailImage,
              mainImage,
              name,
              colorCountOptions,
              cancelQueryString,
              selectedPrintOption,
              hspAvailable,
            } = printPosition;

            this.fullscreenImageDialogRefs[index] = createRef();

            const isMobileOpen =
              (this.isMobile && this.showPrintOptions[index]) || false;

            const showFullScreenImageIcon =
              !this.isMobile || (this.isMobile && this.showPrintOptions[index]);

            return html`
              <div
                class="print-position 
                ${classMap({
                  opened: isMobileOpen,
                  closed: !isMobileOpen,
                  selected: this.selectedPrintPosition[index],
                })}"
              >
                ${showFullScreenImageIcon
                  ? html`
                      <fg-fullscreen-image
                        class="fullscreen-button"
                        .imageUrl=${mainImage}
                        ${ref(this.fullscreenImageDialogRefs[index])}
                      ></fg-fullscreen-image>
                    `
                  : nothing}
                ${thumbnailImage
                  ? html`
                      <fg-image
                        clickable
                        width="96"
                        height="96"
                        src=${thumbnailImage}
                        class="print-option-image"
                        @fg-image-click-event=${(event: Event) => {
                          event.stopPropagation();
                          if (
                            (this.isMobile && this.showPrintOptions[index]) ||
                            !this.isMobile
                          ) {
                            this.fullscreenImageDialogRefs[index]?.value?.open(
                              event,
                            );
                          }
                        }}
                      ></fg-image>
                    `
                  : html`
                      <span class="spacer"></span>
                    `}

                <div class="print-methods-wrapper">
                  <h3 class="h5" @click=${() => this.togglePrintOptions(index)}>
                    ${name} ${this.isMobile ? iconChevron : nothing}
                    ${this.isMobile && this.selectedDescription[index]
                      ? html`
                          <span>${this.selectedDescription[index]}</span>
                        `
                      : nothing}
                  </h3>

                  <div class="print-methods">
                    ${this.isMobile
                      ? html`
                          <select
                            name=${name || 'print-option-select-${index}'}
                            @change=${(event: {
                              target: HTMLSelectElement;
                            }) => {
                              const selectedData = printPosition.methods.find(
                                obj => obj.queryString === event.target.value,
                              );
                              this.handleSelectedPrintPosition(
                                index,
                                true,
                                selectedData?.description,
                                selectedPrintOption?.code,
                              );

                              this.dispatchEvent(
                                new CustomEvent('option-selected', {
                                  composed: true,
                                  detail: {
                                    option: {
                                      queryString: event.target?.value,
                                    },
                                  },
                                }),
                              );

                              if (selectedData === undefined) {
                                this.handleSelectedPrintPosition(
                                  index,
                                  false,
                                  undefined,
                                  selectedPrintOption?.code,
                                );
                                this.dispatchEvent(
                                  new CustomEvent('option-selected', {
                                    composed: true,
                                    detail: {
                                      option: {
                                        queryString: cancelQueryString,
                                      },
                                    },
                                  }),
                                );
                              }
                            }}
                          >
                            <option
                              ?selected=${Object.keys(this.selectedDescription)
                                .length === 0}
                              value=${printPosition.cancelQueryString}
                            >
                              ${t('labels.noPrint')}
                            </option>
                            ${printPosition.methods?.map(
                              printPositionMethod => html`
                                <option
                                  ?selected=${this.selectedDescription[
                                    index
                                  ] === printPositionMethod.description}
                                  value="${printPositionMethod.queryString}"
                                >
                                  ${printPositionMethod.name} &dash;
                                  ${printPositionMethod.description}
                                  ${printPositionMethod.hspAvailable
                                    ? ` -  ${t('labels.24hProductionAvailable')}`
                                    : ''}
                                </option>
                              `,
                            )}
                          </select>
                        `
                      : html`
                          <fg-print-option-select
                            .methods=${printPosition.methods}
                            .name=${name || `print-option-select-${index}`}
                            @option-selected=${(event: CustomEvent) => {
                              this.handleSelectedPrintPosition(
                                index,
                                true,
                                event.detail.description,
                                selectedPrintOption?.code,
                              );
                            }}
                            @no-print-selected=${() => {
                              this.handleSelectedPrintPosition(index, false);

                              this.dispatchEvent(
                                new CustomEvent('option-selected', {
                                  composed: true,
                                  detail: {
                                    option: {
                                      queryString: cancelQueryString,
                                    },
                                  },
                                }),
                              );
                            }}
                          ></fg-print-option-select>
                        `}
                  </div>
                  ${colorCountOptions &&
                  colorCountOptions?.length > 0 &&
                  !(!salable && layout == 'wholesale')
                    ? html`
                        <div class="print-colors">
                          <fg-print-option-color-count
                            ?disabled=${this.isColorInputDisabled(
                              selectedPrintOption,
                            )}
                            .part=${layout}
                            .colorCount=${colorCountOptions}
                          ></fg-print-option-color-count>
                        </div>
                      `
                    : nothing}
                  ${this.isMobile && hspAvailable
                    ? html`
                        <span class="hsp-text">
                          <span>${iconStopwatch}</span>
                          ${t('labels.24hProductionAvailable')}
                        </span>
                      `
                    : nothing}
                </div>
                ${this.selectedPrintPosition[index] &&
                selectedPrintOption?.code &&
                selectedPrintOption &&
                !(!salable && layout == 'wholesale')
                  ? html`
                      <fg-personalisation-status-bar
                        part="wholesale"
                        .personalisationData=${personalisation.getDataWithCode(
                          this.personalisationContext,
                          selectedPrintOption?.code,
                        )}
                        .printOptionName=${name}
                        @open=${() => {
                          if (
                            this.configuratorContext.visualDesigner
                              ?.isVisualDesignerAvailable
                          ) {
                            const selectedPrintOptions = (
                              this.printPositions ?? []
                            ).flatMap(printPosition => {
                              const code =
                                printPosition.selectedPrintOption?.code;
                              return code ? [{ code, colorCount: 1 }] : [];
                            });
                            visualDesigner.providerUpdateEvent(this, {
                              ...this.visualDesignerContext,
                              selectedPrintOptions,
                            });
                          }

                          this.openPersonalisationDialog(selectedPrintOption);
                        }}
                        @remove=${() => {
                          personalisation.providerRemoveEvent(
                            this,
                            selectedPrintOption?.code,
                          );

                          if (
                            this.visualDesignerContext?.selectedPrintOptions?.find(
                              printOption =>
                                printOption.code === selectedPrintOption?.code,
                            ) !== undefined
                          ) {
                            visualDesigner.providerRemoveSelectedPrintOptionEvent(
                              this,
                              this.visualDesignerContext,
                              selectedPrintOption?.code,
                            );
                          }

                          this.resetColorCount(selectedPrintOption?.code);

                          this.requestUpdate();
                        }}
                      ></fg-personalisation-status-bar>
                    `
                  : nothing}
                ${!this.isMobile && hspAvailable
                  ? html`
                      <span class="hsp-text">
                        <span>${iconStopwatch}</span>
                        ${t('labels.24hProductionAvailable')}
                      </span>
                    `
                  : nothing}
              </div>
            `;
          })}

          <fg-personalisation
            ${ref(this.fgPersonalisationDialogRef)}
            .printOption=${this.currentSelectedPrintOption}
            .hasColorChoice=${this.currentSelectedPrintOption?.hasColorChoice}
            .isVisualDesignerAvailable=${Boolean(
              this.configuratorContext.visualDesigner
                ?.isVisualDesignerAvailable,
            )}
            .quantity=${this.configuratorContext.visualDesigner?.quantity}
            @personalisation-completed=${() => this.requestUpdate()}
            @update-visual-designer-color-count=${(
              event: CustomEvent<Array<{ code: string; colorCount: number }>>,
            ) => this.handleVisualDesignerColorCountUpdate(event.detail)}
          ></fg-personalisation>
          ${Object.values(this.selectedPrintPosition).includes(true) &&
          !(!salable && layout === 'wholesale')
            ? html`
                <fg-print-example
                  .configurationId=${configurationId}
                  .productName=${productName}
                  .printExampleData=${this.printExampleData}
                ></fg-print-example>
              `
            : nothing}
        </div>
      </fg-configurator-step>
    `;
  }

  static styles = [
    baseStyles,
    formStyles,
    buttonStyles,
    typographyStyles,
    css`
      .wrapper {
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin-top: 16px;
      }

      .print-position {
        position: relative;
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        border: 1px solid transparent;
        gap: 16px;
        padding: 8px;
        display: grid;
        gap: 16px;
        grid-template-columns: 48px 1fr;
        transition:
          background-color ease-in-out ${ANIMATION_SPEED}ms,
          border-color ease-in-out ${ANIMATION_SPEED}ms;
      }

      .print-position.selected,
      .print-position.opened.selected {
        background-color: var(--fg-success--light);
        border-color: var(--fg-success);
        color: #fff;
      }

      .print-position > fg-image::part(image) {
        aspect-ratio: 1 / 1;
        background-color: #fff;
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        max-height: 48px;
        max-width: 48px;
        object-fit: contain;
      }

      .print-position .spacer {
        width: 48px;
        height: 48px;
        display: block;
      }

      .print-position.opened {
        background-color: #fff;
        box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
      }

      h5 {
        align-items: center;
        display: flex;
        column-gap: 16px;
        flex-wrap: wrap;
        justify-content: space-between;
        line-height: 28px;
        padding-top: 6px;
        padding-bottom: 8px;
        width: 100%;
      }

      .opened h5 {
        padding: 0;
      }

      h5 svg {
        transform: rotate(180deg);
        flex-shrink: 0;
      }

      .opened h5 svg {
        transform: rotate(0deg);
      }

      h5 span {
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
        font-size: 0.875rem;
        font-weight: 400;
        width: 100%;
      }

      .print-methods-wrapper {
        display: flex;
        flex-wrap: wrap;
        column-gap: 16px;
        justify-content: space-between;
      }

      .print-methods-wrapper h5 svg {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
      }

      .print-methods-wrapper.closed {
        min-height: 48px;
      }

      .print-methods {
        align-items: stretch;
        display: none;
        min-width: 230px;
        width: 100%;
        padding-bottom: 8px;
      }

      .print-colors {
        margin-bottom: 8px;
        min-width: 150px;
        display: grid;
        flex-grow: 1;
        height: 100%;
      }

      .opened .print-methods {
        display: flex;
        padding-top: 8px;
      }

      select {
        width: 100%;
        overflow-x: hidden;
      }

      .print-option-personalisation-details {
        color: var(--fg-success--dark, var(--fg-fallback-success--dark));
        display: flex;
        height: 48px;
        align-items: center;
        flex-grow: 1;
        justify-content: end;
      }

      .print-option-personalisation-details span {
        margin-right: 5px;
      }

      .print-option-personalisation-details button.remove {
        color: var(--fg-gray--700, var(--fg-fallback-gray--700));
        height: 24px;
        margin-left: 5px;
        width: 24px;
      }

      .hsp-text {
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
        grid-column-start: 2;
      }

      .fullscreen-button {
        position: absolute;
        top: 12px;
        left: 12px;
        color: var(--fg-secondary, var(--fg-fallback-secondary));
      }

      .opened .print-option-image {
        cursor: pointer;
      }

      fg-personalisation-status-bar {
        margin-top: 0.5rem;
        grid-column: 1 / 3;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        h5 {
          display: block;
        }

        .opened h5 {
          height: 48px;
          width: 100%;
        }

        .print-option-image {
          cursor: pointer;
        }

        .print-methods {
          display: flex;
        }

        .opened .print-methods {
          height: 100%;
          padding-top: 0;
        }

        .fullscreen-button {
          visibility: hidden;
        }

        .print-position:hover .fullscreen-button {
          visibility: visible;
        }

        .print-position {
          align-items: center;
          grid-template-columns: 48px 1fr;
          padding: 8px;
        }

        .print-position .spacer,
        .print-position > fg-image::part(image) {
          width: 48px;
          height: 48px;
          margin-bottom: auto;
        }

        .print-colors {
          margin-bottom: 0;
        }

        .hsp-text svg {
          color: var(--fg-gray--900, var(--fg-fallback-gray--900));
        }
      }

      @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
        h5 {
          padding-top: 10px;
        }

        .print-position {
          grid-template-columns: 96px 1fr;
        }

        .print-position .spacer,
        .print-position > fg-image::part(image) {
          height: 96px;
          max-height: 96px;
          max-width: 96px;
          width: 96px;
        }

        .print-methods-wrapper {
          display: grid;
          grid-template-columns: 160px 1fr 1fr;
          flex-wrap: nowrap;
          justify-content: flex-start;
          align-items: center;
        }

        .print-methods {
          min-width: 250px;
          width: auto;
          padding-bottom: 0;
        }

        .print-position {
          row-gap: 0;
        }
      }
    `,
  ];
}
